import { useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { CopyPhone } from '../components/CopyPhone';
import { ApiError } from '../api/client';
import {
  useAddLeadNote,
  useAssignLead,
  useChangeStatus,
  useLeadStatuses,
  useLeadSubStatuses,
  useSetLeadSubStatus,
  useCampaigns,
  useSetLeadCampaign,
  useCustomFields,
  useLead,
  useLeadTimeline,
  useUpdateLead,
  useUsers,
  useLeadCalls,
} from '../api/hooks';
import { useAuth } from '../auth/AuthContext';
import { CustomFieldSet } from '../components/CustomFieldInput';
import { CallButton, CALL_STATUS_LABEL, callStatusColor, formatBillsec, isLiveStatus } from '../components/CallButton';
import { SearchSelect } from '../components/SearchSelect';
import {
  CustomValue,
  ErrorBanner,
  Modal,
  StatusChip,
  formatDateTime,
  relativeTime,
} from '../components/ui';

const TIMELINE_ICONS: Record<string, string> = {
  lead_created: '✦',
  // A repeat registration folded into this lead rather than becoming a second one.
  lead_reregistered: '↻',
  status_changed: '→',
  assignment: '👤',
  note_added: '✎',
  followup_created: '⏰',
  followup_completed: '✓',
  phone_call: '☎',
  whatsapp: '💬',
  whatsapp_auto: '💬',
  email: '✉',
};

export function LeadDetailPage() {
  const { id } = useParams<{ id: string }>();
  // Set by the New lead form when the number already had a lead.
  const merged = (useLocation().state as { merged?: boolean } | null)?.merged === true;
  const { can } = useAuth();

  const { data: lead, isPending, error } = useLead(id);
  const { data: fields = [] } = useCustomFields();
  const { data: timeline = [] } = useLeadTimeline(id);

  const changeStatus = useChangeStatus();
  const assignLead = useAssignLead();
  // Gated on the permission: a counsellor cannot list users, so fetching
  // unconditionally would 403 on every lead they open.
  const { data: assignees = [] } = useUsers(false, can('lead.assign'));
  const updateLead = useUpdateLead();
  const addNote = useAddLeadNote();
  const { data: subStatuses = [] } = useLeadSubStatuses();
  const { data: statuses = [] } = useLeadStatuses();
  const setSubStatus = useSetLeadSubStatus();
  const setCampaign = useSetLeadCampaign();
  const { data: campaigns = [] } = useCampaigns();

  const [editing, setEditing] = useState(false);
  const [note, setNote] = useState('');
  const [actionError, setActionError] = useState<unknown>(null);

  async function submitNote() {
    if (!id || !note.trim()) return;
    setActionError(null);
    try {
      await addNote.mutateAsync({ id, body: note.trim() });
      setNote('');
    } catch (err) {
      setActionError(err);
    }
  }

  const byKey = useMemo(() => new Map(fields.map((f) => [f.api_key, f])), [fields]);

  // The sub-status list is whatever hangs off the status the lead is in now.
  const currentStatusKey = lead?.status.key.toLowerCase();
  const subStatusOptions = useMemo(
    () =>
      subStatuses
        .filter((s) => s.status_key.toLowerCase() === currentStatusKey)
        .map((s) => ({ value: s.key, label: s.name })),
    [subStatuses, currentStatusKey],
  );

  if (isPending) {
    return (
      <div className="page">
        <div className="skeleton" style={{ height: 26, width: 220, marginBottom: 18 }} />
        <div className="skeleton" style={{ height: 260, borderRadius: 10 }} />
      </div>
    );
  }

  if (error || !lead) {
    return (
      <div className="page">
        <ErrorBanner error={error} />
        <Link className="btn" to="/leads">← Back to leads</Link>
      </div>
    );
  }

  async function onStatusChange(status: string) {
    if (!id || status === lead!.status.key) return;
    setActionError(null);
    try {
      await changeStatus.mutateAsync({ id, status });
    } catch (err) {
      setActionError(err);
    }
  }

  const attribution = lead.attribution as Record<string, unknown>;
  const hasAttribution = Object.keys(attribution).length > 0;

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <Link to="/leads" style={{ fontSize: 12.5, color: 'var(--muted)', textDecoration: 'none' }}>
            ← Leads
          </Link>
          <h1 style={{ marginTop: 4 }}>
            {lead.full_name}
            {lead.is_duplicate && <span className="chip chip-dup" style={{ marginLeft: 10, verticalAlign: 'middle' }}>possible duplicate</span>}
          </h1>
          <p className="sub">
            Created {formatDateTime(lead.created_at)} · last activity {relativeTime(lead.last_activity_at)}
          </p>
        </div>

        <div className="page-head-actions">
          {can('lead.update') && (
            <button className="btn" onClick={() => setEditing(true)}>Edit</button>
          )}
        </div>
      </div>

      {merged && (
        <div className="banner banner-info" role="status">
          <div>
            A lead with this phone number already exists, so no new lead was created. What you entered
            was added to this lead — see “Duplicate enquiry” in its activity.
          </div>
        </div>
      )}
      <ErrorBanner error={actionError} />

      <div className="detail-grid">
        <div style={{ display: 'grid', gap: 18 }}>
          <div className="card">
            <div className="card-head">
              <h2>Contact</h2>
              <div className="card-actions"><StatusChip status={lead.status} /></div>
            </div>
            <div className="card-body">
              <dl className="kv">
                <dt>Phone</dt>
                <dd className="mono">
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <CopyPhone phone={lead.phone}>
                      {lead.phone && <a href={`tel:${lead.phone}`}>{lead.phone}</a>}
                    </CopyPhone>
                    <CallButton leadId={lead.id} phone={lead.phone} />
                  </span>
                </dd>
                <dt>Email</dt>
                <dd className="mono">
                  {lead.email ? <a href={`mailto:${lead.email}`}>{lead.email}</a> : '—'}
                </dd>
                {lead.alternate_phone && (
                  <>
                    <dt>Alternate</dt>
                    <dd className="mono">{lead.alternate_phone}</dd>
                  </>
                )}
                <dt>Source</dt>
                <dd><span className="chip chip-plain">{lead.source.name}</span></dd>
                <dt>Assigned to</dt>
                <dd>
                  {can('lead.assign') ? (
                    <select
                      aria-label="Assign lead to"
                      value={lead.assigned_to?.id ?? ''}
                      disabled={assignLead.isPending}
                      onChange={async (e) => {
                        setActionError(null);
                        try {
                          await assignLead.mutateAsync({ id: lead.id, user_id: e.target.value || null });
                        } catch (err) {
                          setActionError(err);
                        }
                      }}
                    >
                      <option value="">Unassigned</option>
                      {assignees.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.full_name} ({u.lead_count})
                        </option>
                      ))}
                    </select>
                  ) : (
                    lead.assigned_to?.full_name ?? <span style={{ color: 'var(--faint)' }}>Unassigned</span>
                  )}
                  {lead.team && <span className="tag" style={{ marginLeft: 6 }}>{lead.team.name}</span>}
                </dd>
                {lead.assigned_at && (
                  <>
                    <dt>Assigned date & time</dt>
                    <dd>
                      <span title={new Date(lead.assigned_at).toLocaleString()}>
                        {formatDateTime(lead.assigned_at)} ({relativeTime(lead.assigned_at)})
                      </span>
                    </dd>
                  </>
                )}
                {/* Under the owner: who has it, then what state it is in. */}
                <dt>Lead status</dt>
                <dd>
                  <select
                    aria-label="Lead status"
                    value={lead.status.key}
                    disabled={!can('lead.update') || changeStatus.isPending}
                    onChange={(e) => onStatusChange(e.target.value)}
                    style={{ width: 'auto', minWidth: 170 }}
                  >
                    {statuses.map((s) => (
                      <option key={s.key} value={s.key}>{s.name}</option>
                    ))}
                    {/* A lead can still sit in a status deleted since (a restored
                        lead, say). Show it so the select is not blank, but it
                        cannot be chosen again. */}
                    {!statuses.some((s) => s.key === lead.status.key) && (
                      <option value={lead.status.key} disabled>{lead.status.name} (deleted)</option>
                    )}
                  </select>
                </dd>

                {/* The reason under the status. The row is always here, even
                    where the status has no reasons configured: hiding it made
                    the field look missing on the 96% of leads sitting in New.
                    Changing the status clears the value server-side, so what
                    shows here always belongs to the status above. */}
                <dt>Lead sub status</dt>
                <dd>
                  {subStatusOptions.length > 0 ? (
                    <SearchSelect
                      id="lead-sub-status"
                      ariaLabel="Lead sub status"
                      value={lead.sub_status?.key ?? null}
                      options={subStatusOptions}
                      disabled={!can('lead.update') || setSubStatus.isPending}
                      placeholder="Choose a reason…"
                      emptyLabel="No sub status"
                      searchPlaceholder={`Search ${subStatusOptions.length} reasons…`}
                      onChange={async (key) => {
                        if (!id) return;
                        setActionError(null);
                        try {
                          await setSubStatus.mutateAsync({ id, sub_status: key });
                        } catch (err) {
                          setActionError(err);
                        }
                      }}
                    />
                  ) : (
                    <span style={{ color: 'var(--faint)' }}>
                      No sub statuses for “{lead.status.name}”
                    </span>
                  )}
                </dd>

                <dt>Campaign</dt>
                <dd>
                  <select
                    aria-label="Campaign"
                    value={lead.campaign?.id ?? ''}
                    disabled={!can('lead.update') || setCampaign.isPending}
                    onChange={async (e) => {
                      if (!id) return;
                      setActionError(null);
                      try {
                        await setCampaign.mutateAsync({ id, campaign_id: e.target.value || null });
                      } catch (err) {
                        setActionError(err);
                      }
                    }}
                    style={{ width: 'auto', minWidth: 170 }}
                  >
                    <option value="">No campaign</option>
                    {campaigns.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                    {/* Kept on the lead after it was deactivated; shown, not choosable. */}
                    {lead.campaign && !campaigns.some((c) => c.id === lead.campaign!.id) && (
                      <option value={lead.campaign.id} disabled>{lead.campaign.name} (deactivated)</option>
                    )}
                  </select>
                </dd>
              </dl>

              {/* Remarks are written straight after a call, with the lead's
                  number still on screen — so the box lives here, not under the
                  timeline it feeds. */}
              {can('note.create') && (
                <div style={{ marginTop: 18, borderTop: '1px solid var(--rule-soft)', paddingTop: 14 }}>
                  <label htmlFor="lead-note">Add a note</label>
                  <textarea
                    id="lead-note"
                    rows={3}
                    placeholder="Remark from the call…"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    onKeyDown={(e) => {
                      // Enter alone would fight multi-line remarks.
                      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void submitNote();
                    }}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
                    <button
                      className="btn btn-primary btn-sm"
                      disabled={!note.trim() || addNote.isPending}
                      onClick={submitNote}
                    >
                      {addNote.isPending ? <span className="spinner" /> : 'Add note'}
                    </button>
                    <span className="hint" style={{ marginTop: 0 }}>
                      Saved to the activity timeline.
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-head"><h2>Details</h2></div>
            <div className="card-body">
              {fields.filter((f) => f.is_active).length === 0 ? (
                <p style={{ color: 'var(--muted)', margin: 0, fontSize: 13.5 }}>
                  No custom fields configured yet.
                </p>
              ) : (
                <dl className="kv">
                  {fields
                    .filter((f) => f.is_active)
                    .map((f) => (
                      <div key={f.api_key} style={{ display: 'contents' }}>
                        <dt>{f.label}</dt>
                        <dd>
                          <CustomValue apiKey={f.api_key} value={lead.custom_fields[f.api_key]} byKey={byKey} />
                        </dd>
                      </div>
                    ))}
                </dl>
              )}
            </div>
          </div>

          {hasAttribution && (
            <div className="card">
              <div className="card-head">
                <h2>Attribution</h2>
                <div className="card-actions">
                  <span className="chip chip-plain" title="Frozen at capture time, so a renamed campaign never rewrites history">
                    snapshot
                  </span>
                </div>
              </div>
              <div className="card-body">
                <dl className="kv">
                  {Object.entries(attribution)
                    .filter(([, v]) => v !== null && v !== undefined && typeof v !== 'object')
                    .map(([k, v]) => (
                      <div key={k} style={{ display: 'contents' }}>
                        <dt>{k.replace(/_/g, ' ')}</dt>
                        <dd>{String(v)}</dd>
                      </div>
                    ))}
                </dl>
              </div>
            </div>
          )}

          {/* Belongs in the wide column: it carries a five-column table. Placed
              as a sibling of the grid it landed in the 340px sidebar and pushed
              Activity into an implicit third column, overflowing the page. */}
          <CallHistoryCard leadId={lead.id} />
        </div>

        <div className="card">
          <div className="card-head"><h2>Activity</h2></div>
          <div className="card-body">
            {timeline.length === 0 ? (
              <p style={{ color: 'var(--muted)', margin: 0, fontSize: 13.5 }}>Nothing recorded yet.</p>
            ) : (
              <ul className="timeline">
                {timeline.map((entry, i) => (
                  <li key={`${entry.occurred_at}-${i}`}>
                    <span className="tl-dot" aria-hidden="true">{TIMELINE_ICONS[entry.kind] ?? '•'}</span>
                    <div className="tl-body">
                      <p>{entry.description}</p>
                      <div className="tl-meta">
                        {/* actor_name is null for system events — a Meta lead
                            arriving, or an assignment rule firing. */}
                        {entry.actor_name ?? 'System'} · {formatDateTime(entry.occurred_at)}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {editing && (
        <EditLeadModal
          lead={lead}
          onClose={() => setEditing(false)}
          onSaved={() => setEditing(false)}
          update={updateLead}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */

function EditLeadModal({
  lead,
  onClose,
  onSaved,
  update,
}: {
  lead: import('../api/types').Lead;
  onClose: () => void;
  onSaved: () => void;
  update: ReturnType<typeof useUpdateLead>;
}) {
  const { data: fields = [] } = useCustomFields();
  const [core, setCore] = useState({
    full_name: lead.full_name,
    phone: lead.phone ?? '',
    email: lead.email ?? '',
  });
  const [custom, setCustom] = useState<Record<string, unknown>>({ ...lead.custom_fields });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<unknown>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setFieldErrors({});
    try {
      await update.mutateAsync({
        id: lead.id,
        full_name: core.full_name.trim(),
        phone: core.phone.trim() || null,
        email: core.email.trim() || null,
        // PATCH merges: unmentioned keys keep their values, explicit null clears.
        custom_fields: custom,
      });
      onSaved();
    } catch (err) {
      setError(err);
      if (err instanceof ApiError) setFieldErrors(err.fieldErrors);
    }
  }

  return (
    <Modal
      title="Edit lead"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} type="button">Cancel</button>
          <button className="btn btn-primary" form="edit-lead" disabled={update.isPending}>
            {update.isPending ? <span className="spinner" /> : 'Save changes'}
          </button>
        </>
      }
    >
      <ErrorBanner error={error} />
      <form id="edit-lead" onSubmit={submit}>
        <div className={`field${fieldErrors.full_name ? ' invalid' : ''}`}>
          <label htmlFor="e-name">Full name</label>
          <input id="e-name" required value={core.full_name} onChange={(e) => setCore({ ...core, full_name: e.target.value })} />
          {fieldErrors.full_name && <div className="err">{fieldErrors.full_name}</div>}
        </div>
        <div className="grid-2">
          <div className={`field${fieldErrors.phone ? ' invalid' : ''}`}>
            <label htmlFor="e-phone">Phone</label>
            <input id="e-phone" type="tel" value={core.phone} onChange={(e) => setCore({ ...core, phone: e.target.value })} />
            {fieldErrors.phone && <div className="err">{fieldErrors.phone}</div>}
          </div>
          <div className={`field${fieldErrors.email ? ' invalid' : ''}`}>
            <label htmlFor="e-email">Email</label>
            <input id="e-email" type="email" value={core.email} onChange={(e) => setCore({ ...core, email: e.target.value })} />
            {fieldErrors.email && <div className="err">{fieldErrors.email}</div>}
          </div>
        </div>

        <h4 style={{ margin: '18px 0 10px', fontSize: 12, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--muted)' }}>
          Details
        </h4>
        <CustomFieldSet
          fields={fields}
          values={custom}
          onChange={(key, value) => setCustom((c) => ({ ...c, [key]: value }))}
          errors={fieldErrors}
        />
      </form>
    </Modal>
  );
}


/* ------------------------------------------------------------------ */

/**
 * Call history for one lead: every attempt, and what they add up to.
 *
 * Separate from the Activity timeline rather than folded into it. A call has
 * structured columns worth aligning — outcome, talk time, who dialled — and the
 * question a manager actually asks ("how many times did we try, did anyone
 * reach them, for how long?") is answered by the aggregates, which prose in a
 * timeline cannot carry. Calls still write an activity row, so the timeline
 * keeps a readable summary alongside.
 *
 * Visible to anyone who can see the lead: the counsellor who owns it, their
 * manager, and an admin. Scope is enforced server-side through the lead.
 */
function CallHistoryCard({ leadId }: { leadId: string }) {
  const { data, isPending } = useLeadCalls(leadId);

  if (isPending || !data || data.data.length === 0) return null;

  const { data: calls, summary } = data;

  return (
    <div className="card">
      <div className="card-head">
        <h2>Calls</h2>
        <span className="count">{summary.total_calls}</span>
      </div>

      <div className="card-body" style={{ paddingTop: 8 }}>
        {/* The aggregates first: the shape of the relationship before the
            individual attempts. */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(104px, 1fr))',
            gap: 12,
            marginBottom: 16,
          }}
        >
          <Stat label="Attempts" value={String(summary.total_calls)} />
          <Stat
            label="Connected"
            value={String(summary.connected_calls)}
            hint={`${(summary.connect_rate * 100).toFixed(0)}% of attempts`}
            tone={summary.connected_calls > 0 ? 'good' : 'muted'}
          />
          <Stat
            label="Avg talk time"
            value={formatBillsec(summary.avg_billsec)}
            /* Spelled out because an average that silently excluded the misses
               would overstate how reachable this lead is. */
            hint="connected calls only"
          />
          <Stat label="Total talk time" value={formatBillsec(summary.total_billsec)} />
          {summary.longest_billsec !== null && (
            <Stat label="Longest" value={formatBillsec(summary.longest_billsec)} />
          )}
        </div>

        {(summary.unconnected_calls > 0 || summary.unknown_calls > 0) && (
          <p className="hint" style={{ marginTop: 0 }}>
            {summary.unconnected_calls > 0 && (
              <>
                {summary.unconnected_calls} attempt{summary.unconnected_calls === 1 ? '' : 's'} did
                not connect — counted in the rate, excluded from the average.
              </>
            )}
            {summary.unknown_calls > 0 && (
              <>
                {' '}
                {summary.unknown_calls} never reported an outcome and {summary.unknown_calls === 1 ? 'is' : 'are'} excluded
                from the rate entirely, since we cannot say whether {summary.unknown_calls === 1 ? 'it' : 'they'} connected.
              </>
            )}
          </p>
        )}

        <div className="card-scroll">
          <table className="data">
            <thead>
              <tr>
                <th>#</th>
                <th>When</th>
                <th>Agent</th>
                <th>Outcome</th>
                <th className="num">Talk time</th>
              </tr>
            </thead>
            <tbody>
              {calls.map((call, i) => (
                <tr key={call.id}>
                  {/* Numbered oldest-first so "the third call" means the same
                    thing to everyone reading it. */}
                  <td className="num" style={{ color: 'var(--faint)' }}>{calls.length - i}</td>
                  <td>{formatDateTime(call.start_time)}</td>
                  <td>{call.agent_name ?? '—'}</td>
                  <td>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <span className="dot" aria-hidden="true" style={{ background: callStatusColor(call.status) }} />
                      <span style={{ color: callStatusColor(call.status) }}>
                        {CALL_STATUS_LABEL[call.status]}
                      </span>
                      {isLiveStatus(call.status) && <span className="hint" style={{ margin: 0 }}>live</span>}
                      {!isLiveStatus(call.status) && !call.finalized && (
                        <span
                          className="hint"
                          style={{ margin: 0 }}
                          title="No final record arrived for this call, so the duration is unconfirmed"
                        >
                          unconfirmed
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="num mono">{formatBillsec(call.billsec)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: 'good' | 'muted';
}) {
  return (
    <div style={{ background: 'var(--sunken)', borderRadius: 8, padding: '10px 12px', minWidth: 0 }}>
      <div style={{ fontSize: 12, color: 'var(--muted)' }}>{label}</div>
      <div
        style={{
          fontSize: 20,
          fontWeight: 600,
          color: tone === 'good' ? 'var(--good)' : undefined,
          // Long values like "1h 12m 30s" must wrap rather than widen the tile.
          overflowWrap: 'anywhere',
        }}
      >
        {value}
      </div>
      {hint && <div style={{ fontSize: 11, color: 'var(--faint)' }}>{hint}</div>}
    </div>
  );
}
